import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Shell, CTGrid, StatCard } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, BookOpen } from "lucide-react";

const Ranked = ({ title, rows, testid }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testid}>
    <h4 className="text-base font-semibold mb-4">{title}</h4>
    {rows.length === 0 && <p className="text-sm text-slate-500">No data yet</p>}
    <div className="space-y-3">
      {rows.map((r) => (
        <div key={r.key}>
          <div className="flex justify-between text-sm">
            <span className="text-slate-700 truncate pr-2">{r.label}</span>
            <span className="font-semibold">{r.percentage}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full mt-1 overflow-hidden">
            <div className="h-full bg-primary rounded-full" style={{ width: `${r.percentage}%` }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({});
  const [divisions, setDivisions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [tests, setTests] = useState([]);

  const load = async (f = filters) => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    const { data } = await api.get("/analytics/admin", { params });
    setData(data);
  };
  useEffect(() => { load({}); api.get("/subjects").then((r) => setSubjects(r.data)); }, []);

  const set = async (k, v) => {
    const val = v === "__all" ? "" : v;
    const f = { ...filters, [k]: val };
    if (k === "school_id" || k === "class_id") { f.division = ""; }
    if (k === "class_id") { f.chapter_id = ""; f.test_id = ""; }
    setFilters(f);
    if (f.school_id && f.class_id) {
      const r = await api.get("/divisions", { params: { school_id: f.school_id, class_id: f.class_id } });
      setDivisions(r.data);
    }
    if (k === "subject_id" || k === "class_id") {
      const r = await api.get("/chapters", { params: { class_id: f.class_id || undefined, subject_id: f.subject_id || undefined } });
      setChapters(r.data);
    }
    if (k === "chapter_id" && f.chapter_id) {
      const r = await api.get("/tests", { params: { chapter_id: f.chapter_id } });
      setTests(r.data);
    }
    load(f);
  };

  const t = data?.totals;
  return (
    <Shell title="Super Admin" subtitle="Platform-wide analytics & management" nav={
      <div className="hidden md:flex gap-1">
        <Link to="/admin/schools"><Button variant="ghost" size="sm" data-testid="nav-schools"><Building2 className="h-4 w-4 mr-1" />Schools</Button></Link>
        <Link to="/admin/content"><Button variant="ghost" size="sm" data-testid="nav-content"><BookOpen className="h-4 w-4 mr-1" />Content</Button></Link>
      </div>
    }>
      <div className="flex gap-2 md:hidden mb-6">
        <Link to="/admin/schools" className="flex-1"><Button variant="outline" size="sm" className="w-full" data-testid="nav-schools-m">Schools</Button></Link>
        <Link to="/admin/content" className="flex-1"><Button variant="outline" size="sm" className="w-full" data-testid="nav-content-m">Content</Button></Link>
      </div>

      <h2 className="text-2xl sm:text-3xl font-head font-bold">Platform Analytics</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6" data-testid="admin-stats">
        <StatCard label="Schools" value={`${t?.active_schools ?? 0}/${t?.schools ?? 0}`} testid="stat-schools" />
        <StatCard label="Students" value={t?.students ?? 0} testid="stat-students" />
        <StatCard label="Tests" value={t?.tests ?? 0} testid="stat-tests" />
        <StatCard label="Questions" value={t?.questions ?? 0} testid="stat-questions" />
        <StatCard label="Attempts" value={t?.attempts ?? 0} testid="stat-attempts" />
        <StatCard label="Avg %" value={`${t?.avg_percentage ?? 0}%`} testid="stat-avg" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5 mt-6" data-testid="admin-filters">
        <Label className="text-xs uppercase tracking-wide text-slate-500">Filters</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          <Select value={filters.school_id || "__all"} onValueChange={(v) => set("school_id", v)}>
            <SelectTrigger data-testid="filter-school"><SelectValue placeholder="All schools" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All schools</SelectItem>
              {(data?.schools || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.class_id || "__all"} onValueChange={(v) => set("class_id", v)}>
            <SelectTrigger data-testid="filter-class"><SelectValue placeholder="All classes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All classes</SelectItem>
              {(data?.classes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.division || "__all"} onValueChange={(v) => set("division", v)}>
            <SelectTrigger data-testid="filter-division"><SelectValue placeholder="All divisions" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All divisions</SelectItem>
              {divisions.map((d) => <SelectItem key={d.id} value={d.name}>Division {d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.subject_id || "__all"} onValueChange={(v) => set("subject_id", v)}>
            <SelectTrigger data-testid="filter-subject"><SelectValue placeholder="All subjects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All subjects</SelectItem>
              {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.chapter_id || "__all"} onValueChange={(v) => set("chapter_id", v)}>
            <SelectTrigger data-testid="filter-chapter"><SelectValue placeholder="All chapters" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All chapters</SelectItem>
              {chapters.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.test_id || "__all"} onValueChange={(v) => set("test_id", v)}>
            <SelectTrigger data-testid="filter-test"><SelectValue placeholder="All tests" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All tests</SelectItem>
              {tests.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={filters.date_from || ""} onChange={(e) => set("date_from", e.target.value)} data-testid="filter-date-from" />
          <Input type="date" value={filters.date_to || ""} onChange={(e) => set("date_to", e.target.value)} data-testid="filter-date-to" />
        </div>
      </div>

      <h3 className="text-xl font-head font-semibold mt-10 mb-4">CT Competency Performance</h3>
      <CTGrid ct={data?.ct} testid="admin-ct-grid" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <Ranked title="School-wise Performance" rows={data?.by_school || []} testid="by-school" />
        <Ranked title="Class-wise Performance" rows={data?.by_class || []} testid="by-class" />
        <Ranked title="Division-wise Performance" rows={data?.by_division || []} testid="by-division" />
        <Ranked title="Test-wise Performance" rows={data?.by_test || []} testid="by-test" />
      </div>
    </Shell>
  );
}
